import { useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { PublishedThreadsDialog } from './published-threads-dialog';
import { KimThreadStatus } from '../../../../../../../../../../backend/src/matter/subset/thread/kim-thread-status';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeleteKimThread } from '@/api/mutations/useDeleteKimThread';
import { useRenameKimThread } from '@/api/mutations/useRenameKimThread';
import { useTogglePublishedKimThread } from '@/api/mutations/useTogglePublishedKimThreads';
import { useGetKimThreads } from '@/api/queries/useGetKimThreads';
import { useUserContext } from '@/Contexts/User';
import { EditFieldDialogControlled } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

export function NewKimSidebar() {
  const { matterId, subsetId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const { data: threads, isLoading } = useGetKimThreads(matterId, subsetId);
  const { mutate: deleteThread } = useDeleteKimThread(matterId, subsetId);
  const { mutate: renameThread } = useRenameKimThread(matterId, subsetId);
  const { mutate: togglePublished } = useTogglePublishedKimThread(matterId, subsetId);

  const [searchQuery, setSearchQuery] = useState('');
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renamingThread, setRenamingThread] = useState<{ id: string; name: string } | null>(null);

  const [collapsed, setCollapsed] = useState(false);

  const filteredThreads = threads?.filter((thread) => thread.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const publishedThreads = threads?.filter((t) => t.isPublished);
  const userThreads = filteredThreads?.filter((t) => t.createdById === user?.id);

  // threadId is not available from useParams because the sidebar sits outside the :threadId route
  const kimBasePath = `/matters/${matterId}/subsets/${subsetId}/new-kim/`;
  const activeThreadId = location.pathname.startsWith(kimBasePath)
    ? location.pathname.slice(kimBasePath.length).split('/')[0] || undefined
    : undefined;

  const isOnWorkProductPage = location.pathname.includes('/new-kim/work-product');

  return (
    <div
      className={cn(
        collapsed ? 'w-12 rounded-3xl hover:cursor-pointer hover:border-gray-400/60' : 'w-64 rounded-lg',
        'm-2 flex flex-col gap-1.5 border bg-white pt-2 duration-100',
      )}
      onClick={() => setCollapsed(false)}
    >
      <div className="mx-2 flex items-center gap-1.5">
        <Button
          asChild
          size="sm"
          className={cn(
            collapsed ? 'rounded-2xl py-1.5' : 'py-1',
            'grow gap-0 transition-[color,border-color,background-color,padding,border-radius] duration-150',
          )}
        >
          <Link to={`/matters/${matterId}/subsets/${subsetId}/new-kim/`}>
            <span
              className={cn(collapsed ? 'w-0 opacity-0' : 'w-auto pr-1.5 opacity-100', 'line-clamp-1 duration-150')}
            >
              New Thread
            </span>
            <Icon className="lucide--plus" />
          </Link>
        </Button>

        {!collapsed && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant={'ghost'}
                className="px-1 text-gray-500"
                onClick={(event) => {
                  event.stopPropagation();
                  setCollapsed(true);
                }}
              >
                <Icon className="lucide--sidebar-close" />
              </Button>
            </TooltipTrigger>

            <TooltipContent side="bottom">Close sidebar</TooltipContent>
          </Tooltip>
        )}
      </div>

      <div className="flex flex-col gap-0.5 py-0.5">
        <Link
          to={`/matters/${matterId}/subsets/${subsetId}/new-kim/work-product`}
          className={cn(
            'mx-2 flex items-center justify-between gap-2 overflow-hidden rounded-md px-2 py-1.25 text-xs ring-offset-2 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-teal-500',
            isOnWorkProductPage ? 'font-medium text-gray-800' : 'text-gray-600',
          )}
        >
          <div className="flex items-center gap-2">
            <Icon
              className={cn(isOnWorkProductPage && 'text-teal-500', 'lucide--folder-closed text-sm duration-150')}
            />
            <div className={cn(collapsed ? 'opacity-0' : 'opacity-100', 'line-clamp-1 break-all duration-150')}>
              Work Product
            </div>
          </div>

          <div
            className={cn(
              collapsed ? 'opacity-0' : 'opacity-100',
              isOnWorkProductPage ? 'opacity-100' : 'opacity-0',
              'mr-0.25 size-1 rounded-full bg-teal-500 duration-150',
            )}
          />
        </Link>

        <PublishedThreadsDialog publishedThreads={publishedThreads ?? []} collapsed={collapsed} />
      </div>

      <AnimatePresence>
        {!collapsed && (
          <motion.div
            key={'loader'}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className={cn(
              'mx-2 -mb-1.5 flex items-center rounded-md border border-gray-300 bg-white ring-offset-2 transition-colors duration-150 focus-within:ring focus-within:ring-teal-500',
            )}
          >
            <Icon className="lucide--search mx-2.25 mr-1.5 text-xs text-gray-400" />
            <input
              type="text"
              placeholder="Search threads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full py-1.25 text-xs text-gray-900 outline-none placeholder:text-gray-400 focus:outline-none"
            />
          </motion.div>
        )}

        {!collapsed &&
          (isLoading ? (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className="my-8 animate-pulse text-center text-xs text-gray-400"
            >
              Loading Threads...
            </motion.p>
          ) : threads?.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className="my-8 flex flex-col items-center gap-6.5 px-4 text-xs text-gray-400"
            >
              <p className="text-center">No threads yet</p>
              <p className="text-center leading-normal">
                Looking for your old Kim questions?{' '}
                <Link
                  to={`/matters/${matterId}/subsets/${subsetId}/kim`}
                  onClick={() => {
                    trackCustomEvent(TrackingEventsV2.KimReturnToOldExperience, { matterId, subsetId });
                    localStorage.setItem('isUsingNewKim', 'false');
                  }}
                  className="flex items-center gap-1.25 text-balance text-gray-500 underline duration-150 hover:text-gray-700"
                >
                  <Icon className="lucide--undo-2 mb-0.5 -ml-1" />
                  Switch back to the old experience
                </Link>
              </p>
            </motion.div>
          ) : searchQuery && filteredThreads?.length === 0 ? (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.1 }}
              className="my-8 text-center text-xs text-gray-400"
            >
              No matching threads
            </motion.p>
          ) : null)}

        {!collapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.1 }}
            className="flex grow flex-col justify-between overflow-y-auto"
          >
            <div className="flex flex-col gap-1 overflow-y-auto p-2 pt-1 [scrollbar-width:none]">
              {userThreads &&
                userThreads.length > 0 &&
                userThreads.map((thread) => (
                  <div
                    key={thread.id}
                    className={cn(
                      activeThreadId === thread.id
                        ? 'bg-gray-100 font-medium text-gray-900'
                        : 'text-gray-600 hover:bg-gray-50',
                      'flex items-center justify-between rounded-md pr-1 text-xs ring-offset-2 transition-colors first:mt-1 has-[>a:focus-visible]:ring-1 has-[>a:focus-visible]:ring-teal-500',
                    )}
                  >
                    <Link
                      to={`/matters/${matterId}/subsets/${subsetId}/new-kim/${thread.id}`}
                      className={'w-full truncate py-1.5 pl-2 focus-visible:outline-none'}
                    >
                      {thread.name}
                    </Link>

                    {thread.status === KimThreadStatus.Failed && (
                      <Icon className="lucide--circle-alert mr-1 ml-1.5 text-red-500" />
                    )}

                    {thread.isPublished && (
                      <Tooltip>
                        <TooltipTrigger>
                          <Icon className="lucide--users-round mr-1 ml-1.5" />
                        </TooltipTrigger>
                        <TooltipContent>Published</TooltipContent>
                      </Tooltip>
                    )}

                    {thread.createdById === user?.id && (
                      <DropdownMenu>
                        <DropdownMenuTrigger className="rounded-md px-1 ring-offset-2 focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:outline-none">
                          <Icon className="lucide--more-horizontal" />{' '}
                        </DropdownMenuTrigger>

                        <DropdownMenuContent align="start" alignOffset={-8} side="right" sideOffset={8}>
                          <DropdownMenuItem
                            className="text-xs"
                            onClick={() => {
                              setRenamingThread({ id: thread.id, name: thread.name });
                              setRenameDialogOpen(true);
                            }}
                          >
                            <Icon className="lucide--pencil mr-0.25 text-[11px]" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-xs" onClick={() => togglePublished(thread.id)}>
                            <Icon className="lucide--users-round mr-0.25 text-[11px]" />
                            {thread.isPublished ? 'Unpublish' : 'Publish'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-xs"
                            onClick={() =>
                              deleteThread(thread.id, {
                                onSuccess: () => {
                                  if (activeThreadId === thread.id) {
                                    navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/`);
                                  }
                                },
                              })
                            }
                          >
                            <Icon className="lucide--trash mr-0.25 text-[11px]" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ))}
            </div>

            <div className="border-t p-3 pb-4">
              <div className="relative mx-auto mb-3.5 gap-1.75 rounded-md border bg-gray-50 px-2.75 py-1.75 pb-2 text-xs text-gray-500">
                <p className="line-clamp-3">
                  This version of Kim is still in development. Please send any feedback to{' '}
                  <a href="mailto:support@wexler.ai" className="text-blue-500">
                    support@wexler.ai
                  </a>
                </p>
                <div className="absolute -top-2 -right-2 rounded-full border bg-white p-0.75 text-teal-500">
                  <Icon className="lucide--info" />
                </div>
              </div>

              <Link
                to={`/matters/${matterId}/subsets/${subsetId}/kim`}
                className="flex items-center justify-center gap-1.5 text-center text-xs text-gray-500 duration-150 hover:text-gray-700"
                onClick={() => {
                  trackCustomEvent(TrackingEventsV2.KimReturnToOldExperience, { matterId, subsetId });
                  localStorage.setItem('isUsingNewKim', 'false');
                }}
              >
                <Icon className="lucide--undo-2 mb-0.5 -ml-1" />{' '}
                <div className="line-clamp-1">Back to the old Kim experience</div>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {renamingThread && (
        <EditFieldDialogControlled
          open={renameDialogOpen}
          onOpenChange={setRenameDialogOpen}
          title="Rename thread"
          description="Enter a new name for this thread."
          fieldLabel="Name"
          currentValue={renamingThread.name}
          onSave={(name) => {
            trackCustomEvent(TrackingEventsV2.KimRenameThread, { matterId, subsetId, threadId: renamingThread.id });
            renameThread({ threadId: renamingThread.id, name });
          }}
        />
      )}
    </div>
  );
}
